// TODO: Fix all linting warnings in layout-lib #36010
/* eslint-disable */
import { Observable, ReplaySubject, BehaviorSubject, Subscription, combineLatest, of } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { Menu } from './menu.model';
import { OnDestroy } from '@angular/core';
import { Router, NavigationExtras, UrlTree, UrlSegment, PRIMARY_OUTLET } from '@angular/router';
import { RenderableMenuHelper } from './renderable-menu.helper';

export class RenderableMenu {
  private readonly subscriptions: Subscription[] = [];
  private readonly _children$ = new ReplaySubject<RenderableMenu[]>(1);
  private readonly _isMatch$: Observable<boolean>;
  private readonly _isActive$: Observable<boolean>;
  private readonly _isCollapsed$: BehaviorSubject<boolean>;
  private readonly _suppressNavigation$: BehaviorSubject<boolean>;

  constructor(
    private readonly router: Router,
    private readonly menu: Menu,
    private readonly parentRouterLink: any[],
    currentUrl: Observable<string>
  ) {
    this._isMatch$ = currentUrl.pipe(
      map((url) => {
        return RenderableMenuHelper.isItemMatched(router, this, url);
      })
    );
    this._isActive$ = currentUrl.pipe(
      map((url) => {
        return RenderableMenuHelper.isItemActive(router, this, url);
      })
    );
    this._isCollapsed$ = new BehaviorSubject<boolean>(true);
    this._suppressNavigation$ = new BehaviorSubject<boolean>(this.menu.suppressNavigation);

    this.subscriptions.push(
      combineLatest([this.children$, this.isMatched$, currentUrl])
        .pipe(
          // The url should contain more than one character, as its value will be '/' at navigation or page load.
          // Also at the root level ('/') it is not necessary to check the collapse state for children.
          // Please note: this filter prevents collapsing menu items when navigating away; it might be something we want to change later on.
          filter(([children, isMatch, url]) => url && url.length > 1 && children && children.length > 0 && isMatch)
        )
        .subscribe(([children, _isMatch, url]) => {
          const canMatchAnyChild =
            children && children.some((child) => RenderableMenuHelper.isItemMatched(router, child, url));
          this._isCollapsed$.next(!canMatchAnyChild);
        })
    );
  }

  get hidden(): Observable<boolean> {
    return this.menu.hidden ?? of(false);
  }

  get title(): string {
    return this.menu.title;
  }

  get routerLink(): any[] {
    const parentRouterLinkArray: any[] = RenderableMenuHelper.getRouterLinkArray(this.parentRouterLink);
    const currentRouterLinkArray: any[] = RenderableMenuHelper.getRouterLinkArray(this.menu.routerLink);
    return RenderableMenuHelper.constructRouterLink(currentRouterLinkArray, parentRouterLinkArray);
  }

  public loadChildren(children: RenderableMenu[]) {
    this._children$.next(children || []);
  }

  public loadLazyChildren(children$: Observable<RenderableMenu[]>) {
    children$.subscribe(this._children$);
  }

  public toggleCollapseChildren() {
    const collapse = !this._isCollapsed$.getValue();
    this._isCollapsed$.next(collapse);
  }

  get fullPath(): string {
    return this.urlTree.toString();
  }

  get url(): string {
    return this.router.serializeUrl(this.urlTree);
  }

  get urlTree(): UrlTree {
    return this.router.createUrlTree(RenderableMenuHelper.getRouterLinkArray(this.routerLink), this.navigationExtras);
  }

  get urlSegments(): UrlSegment[] {
    return (this.urlTree.root || { segments: [] }).segments;
  }

  get navigationExtras(): NavigationExtras {
    return this.menu.navigationExtras || undefined;
  }

  get children$(): Observable<RenderableMenu[]> {
    return this._children$.asObservable();
  }

  get isMatched$(): Observable<boolean> {
    return this._isMatch$;
  }

  get isActive$(): Observable<boolean> {
    return this._isActive$;
  }

  get isCollapsed$(): BehaviorSubject<boolean> {
    return this._isCollapsed$;
  }

  get suppressNavigation$(): BehaviorSubject<boolean> {
    return this._suppressNavigation$;
  }

  // ! Removed `implements OnDestroy` : not an angular component
  // TODO: Find way to drop subscriptions
  // ngOnDestroy(): void {
  //   this.subscriptions.forEach(subscription => subscription.unsubscribe());
  // }
}
